import { useState, useEffect, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  useParams,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Auth from "./components/auth/Auth";
import { signOut } from "firebase/auth";
import { Box } from "@mui/material";
import { FirebaseProvider, useFirebase } from "./contexts/FirebaseContext";
import { FileActionsProvider } from "./contexts/FileActionsContext";
import { RoleProvider } from "./contexts/RoleContext";
import { getTenantConfig } from "./utils/firebase/config";
import Header from "./components/layout/Header";
import PublicHeader from "./components/layout/Header/PublicHeader";
import LoadingSpinner from "./components/ui/LoadingSpinner";

// Code splitting / lazy loading for performance optimization
const Storage = lazy(() => import("./components/pages/Storage"));
const AdminPanel = lazy(() => import("./components/pages/AdminPanel"));
const SharedView = lazy(() => import("./components/pages/SharedView"));
const UnauthorizedPage = lazy(() => import("./components/pages/UnauthorizedPage"));
const ShareManager = lazy(() => import("./components/common/ShareManager"));
const PublicShare = lazy(() => import("./components/pages/PublicShare/PublicShare"));
const PasswordValidationModal = lazy(() =>
  import("./components/common/PasswordValidationModal")
);

// Componente para o layout público
function PublicAppContent() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "100dvh",
        maxHeight: "100dvh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "rgb(240, 242, 245)",
      }}
    >
      <PublicHeader />
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto", width: "100%", minWidth: 0 }}>
        <Suspense fallback={<LoadingSpinner message="Carregando visualização..." />}>
          <PublicShare />
        </Suspense>
      </Box>
    </Box>
  );
}

// Componente principal da aplicação com sidenav
function MainAppContent() {
  const location = useLocation();
  const [shareManagerOpen, setShareManagerOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  const openShareManager = (target) => {
    setShareTarget(target);
    setShareManagerOpen(true);
  };

  const closeShareManager = () => {
    setShareTarget(null);
    setShareManagerOpen(false);
  };

  const getCurrentPageComponent = () => {
    const path = location.pathname;
    if (path.includes("/admin")) return <AdminPanel />;
    if (path.includes("/shared")) return <SharedView />;
    if (path.includes("/unauthorized")) return <UnauthorizedPage />;
    return <Storage onShare={openShareManager} />;
  };

  return (
    <Box sx={{ display: "flex", height: "100%", width: "100%", minHeight: 0, overflow: "hidden" }}>
      {/* Sidebar 
      <Sidebar openShareManager={openShareManager} />
      */}

      {/* Conteúdo principal */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          backgroundColor: "rgb(240, 242, 245)",
          overflow: "hidden",
        }}
      >
        {/* Conteúdo da página */}
        <Box sx={{ flexGrow: 1, minHeight: 0, height: "100%", width: "100%", minWidth: 0 }}>
          <Suspense fallback={<LoadingSpinner message="Carregando..." />}>
            {getCurrentPageComponent()}
          </Suspense>
        </Box>
      </Box>

      {/* Share Manager */}
      <Suspense fallback={null}>
        <ShareManager
          open={shareManagerOpen}
          onClose={closeShareManager}
          file={shareTarget?.type === "file" ? shareTarget : null}
          folder={shareTarget?.type === "folder" ? shareTarget : null}
        />
      </Suspense>
    </Box>
  );
}

// Novo componente para o conteúdo do tenant
function TenantContent({ tenant }) {
  const { isAuthorizedUser, loadingAuthUser, auth } = useFirebase();
  const [isValidated, setIsValidated] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [firebaseApp, setFirebaseApp] = useState(null);

  // Verificar se o tenant requer validação por senha
  useEffect(() => {
    const tenantConfig = getTenantConfig(tenant);
    if (tenantConfig?.password) {
      // Verificar se já foi validado nesta sessão
      const sessionValidated = sessionStorage.getItem(
        `tenant_${tenant}_validated`,
      );
      if (sessionValidated === "true") {
        setIsValidated(true);
      } else {
        setShowPasswordModal(true);
      }
    } else {
      // Tenant não requer senha
      setIsValidated(true);
    }
  }, [tenant]);

  useEffect(() => {
    if (auth) {
      setFirebaseApp(auth?.app);
    }
  }, [auth]);

  const handlePasswordValidationSuccess = () => {
    setIsValidated(true);
    setShowPasswordModal(false);
  };

  const handlePasswordValidationError = (error) => {
    console.error("Erro na validação:", error);
  };

  const handleSignOut = async () => {
    try {
      if (auth) {
        await signOut(auth);
        sessionStorage.removeItem(`tenant_${tenant}_validated`);
      }
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  if (loadingAuthUser) {
    return <LoadingSpinner message="Verificando autorização..." />;
  }

  return (
    <>
      <Suspense fallback={null}>
        <PasswordValidationModal
          open={showPasswordModal}
          tenant={tenant}
          onValidationSuccess={handlePasswordValidationSuccess}
          onValidationError={handlePasswordValidationError}
          firebaseApp={firebaseApp}
        />
      </Suspense>
      {isValidated && (
        <RoleProvider>
          <div className="App">
            {auth?.currentUser ? (
              isAuthorizedUser ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100dvh",
                    maxHeight: "100dvh",
                    overflow: "hidden",
                  }}
                >
                  <FileActionsProvider>
                    <Header handleSignOut={handleSignOut} />
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        minHeight: 0,
                        height: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <Routes>
                        <Route path="/*" element={<MainAppContent />} />
                      </Routes>
                    </Box>
                  </FileActionsProvider>
                </Box>
              ) : (
                <Suspense fallback={<LoadingSpinner message="Carregando..." />}>
                  <UnauthorizedPage />
                </Suspense>
              )
            ) : (
              <Auth />
            )}
          </div>
        </RoleProvider>
      )}
    </>
  );
}

// Componente para gerenciar cada tenant
function TenantApp() {
  const { tenant } = useParams();

  return (
    <FirebaseProvider tenant={tenant}>
      <TenantContent tenant={tenant} />
    </FirebaseProvider>
  );
}

// Componente principal com roteamento
function App() {
  return (
    <Routes>
      {/* Rotas públicas - com FirebaseProvider próprio */}
      <Route
        path="/public/*"
        element={
          <FirebaseProvider tenant="default">
            <FileActionsProvider isPublicContext={true}>
              <PublicAppContent />
            </FileActionsProvider>
          </FirebaseProvider>
        }
      />

      {/* Rota padrão - redireciona para /default */}
      <Route path="/" element={<Navigate to="/default" replace />} />

      {/* Rota para cada tenant com sistema de roles */}
      <Route path="/:tenant/*" element={<TenantApp />} />
    </Routes>
  );
}

export default App;
